import type { KontoMitarbeiterOption, Permission, Principal } from '@asas/domain';

import type { FehlerBefund } from './daten/fehler';

/**
 * Konto ↔ Mitarbeiter in der Nutzerverwaltung (AC-107-1) als reine Logik —
 * ohne React, ohne Netz. Den Namensvorschlag rechnet
 * `@asas/domain/konto-mitarbeiter`.
 */

/** Das Recht hinter `principals.linkEmployee` und `principals.employeeOptions` (E2). */
export const KONTO_PFLEGERECHT: Permission = 'principals:manage';

/**
 * Bietet die Nutzerverwaltung das Verknüpfen an? Nur mit `principals:manage` —
 * ohne das Recht schriebe schon das Laden der Auswahl eine Audit-Zeile.
 */
export function darfKontenVerknuepfen(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(KONTO_PFLEGERECHT);
}

/**
 * Die Auswahl nach einer Verknüpfung, aus der SERVERANTWORT (AC-016): die
 * vorherige Person des Kontos ist wieder frei, die neue hängt an ihm.
 */
export function optionenNachVerknuepfung(
  optionen: readonly KontoMitarbeiterOption[],
  vorher: Principal,
  nachher: Principal,
): KontoMitarbeiterOption[] {
  return optionen.map((option) => {
    if (option.id === nachher.mitarbeiter?.id) return { ...option, verknuepftMit: nachher.id };
    if (option.id === vorher.mitarbeiter?.id) return { ...option, verknuepftMit: null };
    return option;
  });
}

/**
 * Die Meldung zu einem gescheiterten Verknüpfen. Ein Konflikt heißt hier immer
 * „die Person hängt schon an einem anderen Konto“ — die Schutzregeln der übrigen
 * Kontoaktionen gelten nicht. Das andere Konto nennt die Oberfläche aus der
 * eigenen Liste; die Meldung des Servers wird nicht durchgereicht.
 */
export function meldungFuerVerknuepfung(
  befund: FehlerBefund,
  person: KontoMitarbeiterOption | undefined,
  principals: readonly Principal[],
): string {
  if (befund.art !== 'konflikt') return befund.meldung;

  const anderes = principals.find((principal) => principal.id === person?.verknuepftMit);
  const name = anderes === undefined ? null : (anderes.displayName ?? anderes.subject);

  return name === null
    ? 'Die Person ist schon mit einem anderen Konto verknüpft. Bitte dort zuerst lösen.'
    : `Die Person ist schon mit dem Konto „${name}“ verknüpft. Bitte dort zuerst lösen.`;
}
